import {
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControlType,
  ReportSectionDataSection,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';

export class HistoricalOwnershipConfiguration extends ReportSectionConfigurationBase {
  readonly reportViewModelSections = [ReportViewModelSection.InvestorProfile_HistoricalOwnership];
  typeName = 'HistoricalOwnershipConfiguration';
  readonly reportSection = ReportSection.HistoricalOwnership;
  readonly applicableReportTypes = [ReportType.Contact, ReportType.Institution];
  maxSize = 4;
  defaultSize = 4;
  readonly availableDataSections: ReportSectionDataSection[] = [
    {
      name: 'historicalOwnershipGraph',
      displayName: 'Graph',
      isSelected: true,
      canConfigure: true
    },
    {
      name: 'historicalOwnershipGrid',
      displayName: 'Ownership History',
      type: ReportSectionConfigurationControlType.Table,
      isSelected: true,
      canConfigure: true,
      subSections: [
        {
          name: 'historicalOwnershipDate',
          displayName: 'Date',
          isSelected: true,
          canConfigure: false,
          unit: 2,
          property: 'portfolioDate',
          isStatic: true,
          type: ReportSectionConfigurationControlType.Date
        },
        {
          name: 'historicalOwnershipReportedDate',
          displayName: 'Reported date',
          isSelected: true,
          canConfigure: true,
          unit: 2,
          property: 'holdingDate',
          type: ReportSectionConfigurationControlType.Date
        },
        {
          name: 'historicalOwnershipShares',
          displayName: 'Shares',
          isSelected: true,
          canConfigure: false,
          unit: 2,
          property: 'shares',
          isStatic: true,
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'historicalOwnershipShareChange',
          displayName: 'Share Change',
          isSelected: true,
          canConfigure: true,
          unit: 2,
          property: 'sharesChange',
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'historicalOwnershipValue',
          displayName: 'Value USD, mm',
          isSelected: true,
          canConfigure: true,
          unit: 2,
          property: 'value',
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'historicalOwnershipMarketValueChange',
          displayName: 'Market Value Change USD, mm',
          isSelected: true,
          canConfigure: true,
          unit: 2,
          property: 'valueChange',
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'historicalOwnershipShareChangeValue',
          displayName: 'Share change value USD, mm',
          isSelected: true,
          canConfigure: true,
          unit: 2,
          property: 'sharesChangeValue',
          type: ReportSectionConfigurationControlType.Custom
        },
        {
          name: 'historicalOwnershipSharePrice',
          displayName: 'Share price USD',
          isSelected: true,
          canConfigure: true,
          unit: 1.5,
          property: 'sharePrice',
          type: ReportSectionConfigurationControlType.Number
        },
        {
          name: 'historicalOwnershipSource',
          displayName: 'Source',
          isSelected: true,
          canConfigure: true,
          unit: 1.5,
          property: 'source',
          type: ReportSectionConfigurationControlType.Text
        }
      ]
    }
  ];

  get minSize(): number {
    const selectedSections = this.availableDataSections.filter(section => section.isSelected);
    if (selectedSections.length === 0) {
      return 0;
    }

    const graphSelected = this.availableDataSections.some(
      section => section.isSelected && section.name === 'historicalOwnershipGraph'
    );
    // If only the graph is selected, return 3
    if (graphSelected) {
      return 3;
    }

    // If the grid is selected, check the total number of units selected
    const gridSection = this.availableDataSections.filter(
      section => section.isSelected && section.name === 'historicalOwnershipGrid'
    );
    if (gridSection.length === 1) {
      const totalUnits = gridSection[0]?.subSections
        ?.filter(s => s.isSelected)
        .reduce((sum, subSection) => sum + (subSection.unit || 0), 0);

      if (totalUnits && totalUnits >= 10.5) {
        return 3;
      }
    }
    return 2;
  }

  get name(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'contactHistoricalOwnership';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'institutionHistoricalOwnership';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get displayName(): string {
    if (this.appliedReportType === ReportType.Contact || this.appliedReportType === ReportType.Institution) {
      return 'Historical Ownership';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get nodataLocalizationKeyName(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'primaryInstitutionHistoricalOwnership';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'historicalOwnership';
    } else {
      throw new Error('Report Type not supported');
    }
  }
}

export interface Holding {
  id: number;
  holderId: number;
  securityId: number;
  holderRank: number;
  value: number;
  valueChange: number;
  shares: number;
  sharesChange: number;
  sharesChangeValue: number;
  holdingDate: string;
  portfolioDate: string;
  source: string;
  portfolioPct: string;
  movement: string;
  securityIssuerName: string;
  isCarryForward: boolean;
  sharePrice: number;
  equityAssets: string;
  securitySymbol: string;
}

export interface HistoricalOwnershipDetailsData {
  [ReportViewModelSection.InvestorProfile_HistoricalOwnership]: {
    holdings: Holding[];
    isSurveillance: boolean;
    currencyCode: string;
    culture: string;
    language: string;
    sectionName: { key: string };
  };
}
